import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import EditDeterminantsForm from 'components/modals/referral/form/EditDeterminantsForm';
import ReferralModal from 'components/modals/referral/SendReferralModal';
import ReferralSummaryLauncher from 'components/referral/ReferralSummaryLauncher';
import ReferralSummarySlideout from 'components/referral/summary/Slideout';
import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import { useEpisode } from 'context/episode';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import { useReferral } from 'context/review/referral';
import { useReferralId } from 'hooks/useReferralId';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import LocationEpisode from 'models/LocationEpisode';
import { PERMISSIONS, PRODUCT_ACCESSORS } from 'models/Profile';
import Review, { ReviewStatus } from 'models/Review';
import Referral from 'models/reviews/Referral';
import { createReviewActivity } from 'services/api/activity';
import { locationEpisodeQueryKeys } from 'services/api/locationEpisodes';
import { referralQueryKeys } from 'services/api/reviews';
import { indexReviews } from 'services/api/reviews';
import { FilterOption } from 'stores/types';
import colors from 'styles/theme/colors';

import ActivityList from '../patient/blurbs/activities/ActivityList';
import NewActivity from '../patient/blurbs/NewActivity';

import AcceptedPlan from './blurbs/AcceptedPlan';
import ExpectedDischarge from './blurbs/ExpectedDischarge';
import PatientContactInformation from './blurbs/PatientContactInformation';
import { ReferralBanner } from './ReferralBanner';

function ReferralOptionLabel(referral: Referral) {
  if (!referral.id) return null;
  return (
    <>
      <span style={{ fontWeight: 'bold' }}>{referral.statusLabel} </span>- {referral.group?.name} (
      {referral.group?.groupType.displayName})
    </>
  );
}

const ReferralPage = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { locationEpisode, loading: locationEpisodeLoading, selectedLocationEpisodeId } = useSelectedLocationEpisode();
  const referralId = useReferralId();
  const { referral, referralLoading, setSelectedReferralId } = useReferral();
  const { episode } = useEpisode();

  const showEdit =
    !!episode &&
    !!locationEpisode &&
    profile.has(PERMISSIONS.reviewReferralCreate, { episode, locationEpisode }) &&
    referral?.status === ReviewStatus.PENDING;

  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
  const [showDeterminantsEdit, setShowDeterminantsEdit] = useState(false);

  const healthPlanClassification = locationEpisode?.planTypeClassification?.parentClassification;

  const queryClient = useQueryClient();

  const queryParams = useMemo(
    () => ({
      filters: {
        type: new FilterValueArray(['referral' as FilterOption]),
        locationEpisodeId: new FilterValueArray([(selectedLocationEpisodeId || '') as FilterOption]),
      },
      sortBy: 'createdAt desc',
      include: 'group.group_type',
    }),
    [selectedLocationEpisodeId]
  );

  const { data: referrals, isFetching: referralsLoading } = useQuery({
    queryKey: referralQueryKeys.index({ ...queryParams }),
    queryFn: ({ signal }) => indexReviews(queryParams, signal),
    enabled: !!locationEpisode?.id,
  });

  const numReferrals = referrals?.data?.length ?? 0;

  const referralBaseRoute = `/patients/${episode?.id}/referrals`;

  const navigateToReferralRoute = useCallback(
    (route: string) => navigate(`${referralBaseRoute}/${route}`),
    [referralBaseRoute, navigate]
  );

  useEffect(() => {
    if (referralId) {
      setSelectedReferralId(referralId);
    } else if (!referralsLoading && referrals && numReferrals) {
      navigateToReferralRoute(referrals.data[0].id);
    }
  }, [referralId, referralsLoading, referrals, numReferrals, setSelectedReferralId, navigateToReferralRoute]);

  const [showReferralModal, setShowReferralModal] = useState(false);

  const baseConditionals =
    episode && !!locationEpisode && locationEpisode.active && !locationEpisode.archived && !episode?.archived;

  const showReferralActivityInput = baseConditionals && referral?.committed;

  const loading = (locationEpisodeLoading || (numReferrals && referralLoading) || referralsLoading) && !isSlideoutOpen;

  const showAcceptedPlan =
    baseConditionals && !loading && referral?.group?.hasHealthPlanClassification(healthPlanClassification?.id);

  const removeHasNewChanges = useCallback(() => {
    if (!loading && locationEpisode?.id && locationEpisode?.latestReferralReview?.hasNewChanges) {
      // Surgically update the location episode's hasNewChanges flag without invalidating everything
      queryClient.setQueriesData(
        { queryKey: locationEpisodeQueryKeys.show({ id: locationEpisode.id }) },
        (prevData: any) => {
          if (!prevData || !prevData.latestReferralReview) return prevData;
          const referralId = prevData.latestReferralReview.id;

          return new LocationEpisode({
            ...prevData,
            reviews: prevData.reviews.map((review: Review) =>
              review.id === referralId ? { ...review, hasNewChanges: false } : review
            ),
          });
        }
      );
    }
  }, [loading, locationEpisode?.id, locationEpisode?.latestReferralReview?.hasNewChanges, queryClient]);

  const handleReferralModalSuccess = () => {
    navigate(referralBaseRoute);
    setShowReferralModal(false);
  };

  const newReferralAppropriate = useMemo(() => {
    return !locationEpisode?.discharged && !locationEpisode?.archived && !episode?.archived && locationEpisode?.latest;
  }, [locationEpisode, episode]);

  const isReferralSummarized = !!referral?.summary;
  const referralSummaryFileCount = referral?.attachments.length ?? 0;

  const isEligibleForSummary = !!referralSummaryFileCount && profile.referralSummarizationEnabled;

  const shouldOpenSummarySlideout = isEligibleForSummary && pathname.includes('/summary');

  useLayoutEffect(() => {
    if (shouldOpenSummarySlideout) {
      setIsSlideoutOpen(true);
    } else {
      setIsSlideoutOpen(false);
    }
  }, [shouldOpenSummarySlideout]);

  const handleOpenSlideout = () => {
    if (referral) {
      navigateToReferralRoute(`${referral.id}/summary`);
      setIsSlideoutOpen(true);
    }
  };

  const handleCloseSlideout = () => {
    if (referral) {
      navigateToReferralRoute(referral.id);
      setIsSlideoutOpen(false);
    }
  };

  return (
    <>
      <ResponsiveGrid isNavBarOpen={!!episode}>
        <LeftColumn>
          {showAcceptedPlan && (
            <AcceptedPlan planName={healthPlanClassification?.name ?? ''} groupName={referral?.group?.name ?? ''} />
          )}
          <ExpectedDischarge
            showEdit={showEdit}
            date={referral?.data.expectedDischargeDate ?? ''}
            setShowDeterminantsEdit={setShowDeterminantsEdit}
          />
          <PatientContactInformation
            address={{
              address1: referral?.data.address1 ?? '',
              address2: referral?.data.address2 ?? '',
              city: referral?.data.city ?? '',
              state: referral?.data.state ?? '',
              zip: referral?.data.zip ?? '',
            }}
            phone={referral?.data.phoneNumber ?? ''}
            showEdit={showEdit}
            setShowDeterminantsEdit={setShowDeterminantsEdit}
          />
        </LeftColumn>
        <RightColumn>
          {loading ? (
            <LoadingContainer>
              <CircularProgress color={colors.primaryBlue} />
            </LoadingContainer>
          ) : (
            <>
              {locationEpisode && (
                <>
                  {referral && (
                    <>
                      {(newReferralAppropriate || (referrals?.data && referrals?.data.length > 1)) && (
                        <AccessGuard
                          accessor={PERMISSIONS.reviewReferralCreate}
                          skipPermissionCheck={numReferrals > 1}
                          locationEpisode={locationEpisode}
                          episode={episode}>
                          <SelectContainer>
                            {numReferrals > 1 && (
                              <Select<Review>
                                options={referrals?.data ?? []}
                                onChange={(referral) => navigateToReferralRoute(referral.id)}
                                getOptionValue={({ id }) => id}
                                formatOptionLabel={ReferralOptionLabel}
                                value={referrals?.data?.find((rev) => rev.id === referral?.id)}
                              />
                            )}
                            {newReferralAppropriate && (
                              <AccessGuard
                                accessor={PERMISSIONS.reviewReferralCreate}
                                locationEpisode={locationEpisode}
                                episode={episode}>
                                <Button
                                  variant='primary'
                                  onClick={() => setShowReferralModal(true)}
                                  disabled={locationEpisode.latestReferralReview?.committed}>
                                  Send New Referral
                                </Button>
                              </AccessGuard>
                            )}
                          </SelectContainer>
                        </AccessGuard>
                      )}
                      {isEligibleForSummary && (
                        <AccessGuard accessor={PRODUCT_ACCESSORS.referralSummarizationEnabled}>
                          <ReferralSummaryLauncher
                            fileCount={referralSummaryFileCount}
                            isSummarized={isReferralSummarized}
                            onLaunch={handleOpenSlideout}
                          />
                        </AccessGuard>
                      )}
                    </>
                  )}
                  <ReferralBanner
                    review={referral}
                    profile={profile}
                    episode={episode}
                    locationEpisode={locationEpisode}
                  />
                  {referral && showReferralActivityInput && (
                    <AccessGuard
                      accessor={PERMISSIONS.activityNoteCreate}
                      locationEpisode={locationEpisode}
                      episode={episode}>
                      <NewActivity
                        canElevate={false}
                        episode={episode}
                        locationEpisode={locationEpisode}
                        taggableUserProps={{ onReview: referral?.id }}
                        mutationFn={createReviewActivity}
                        newNoteProps={{ reviewId: referral?.id }}
                      />
                    </AccessGuard>
                  )}
                  {referral && (
                    <ActivityList
                      episode={episode}
                      profile={profile}
                      locationEpisode={locationEpisode}
                      reviewId={referral?.id}
                      activitiesParameters={{ reviewId: referral?.id, viewOnly: true }}
                      onInitialLoad={removeHasNewChanges}
                    />
                  )}
                </>
              )}
            </>
          )}
        </RightColumn>
      </ResponsiveGrid>
      {showReferralModal && locationEpisode && episode && (
        <ReferralModal
          onCancel={() => setShowReferralModal(false)}
          onSuccess={handleReferralModalSuccess}
          locationEpisode={locationEpisode}
          episode={episode}
          patientName={`${episode?.patient.name}`}
        />
      )}
      {showDeterminantsEdit && (
        <EditDeterminantsForm
          initialPhoneNumber={referral?.data.phoneNumber ?? ''}
          referral={referral as Referral}
          setShowDeterminantsEdit={setShowDeterminantsEdit}
        />
      )}
      <ReferralSummarySlideout open={isSlideoutOpen} onClose={handleCloseSlideout} />
    </>
  );
};

const SelectContainer = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: flex-end;

  > div {
    flex: 1;
  }
`;

const ResponsiveGrid = styled.div<{ isNavBarOpen?: boolean }>`
  @media (max-width: ${(props) => (props.isNavBarOpen ? '1050px' : '800px')}) {
    flex-direction: column;
    gap: 24px;
  }
  display: flex;
  flex: 1;
  gap: 36px;
`;

const ResponsiveColumn = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const LeftColumn = styled(ResponsiveColumn)`
  width: 36%;

  @media (max-width: 1050px) {
    width: 100%;
  }
`;

const RightColumn = styled(ResponsiveColumn)`
  flex: 1;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

export default ReferralPage;
